import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMousePosition } from "@/hooks/useMousePosition";

const CustomCursor = () => {
  const { x, y } = useMousePosition();
  const [isHovering, setIsHovering] = useState(false);
  const [cursorText, setCursorText] = useState("");
  const [cursorVariant, setCursorVariant] = useState("default");

  useEffect(() => {
    const handleMouseEnter = (e) => {
      if (!e.target || typeof e.target.matches !== 'function') return;

      if (e.target.matches('button, a, [data-cursor="pointer"]')) {
        setIsHovering(true);
        setCursorVariant("pointer");
        setCursorText(e.target.dataset?.cursorText || "");
      } else if (e.target.matches('[data-cursor="text"]')) {
        setCursorVariant("text");
        setIsHovering(true);
      } else if (e.target.matches('[data-cursor="view"]')) {
        setCursorVariant("view");
        setCursorText("View");
        setIsHovering(true);
      }
    };

    const handleMouseLeave = (e) => {
      if (!e.target || typeof e.target.matches !== 'function') return;

      if (e.target.matches("button, a, [data-cursor]")) {
        setIsHovering(false);
        setCursorVariant("default");
        setCursorText("");
      }
    };

    // Add event listeners
    document.addEventListener("mouseenter", handleMouseEnter, true);
    document.addEventListener("mouseleave", handleMouseLeave, true);

    return () => {
      document.removeEventListener("mouseenter", handleMouseEnter, true);
      document.removeEventListener("mouseleave", handleMouseLeave, true);
    };
  }, []);

  const variants = {
    default: {
      scale: 1,
      backgroundColor: "rgba(16, 185, 129, 0.1)",
      border: "2px solid rgba(16, 185, 129, 0.3)",
    },
    pointer: {
      scale: 1.5,
      backgroundColor: "rgba(16, 185, 129, 0.2)",
      border: "2px solid rgba(16, 185, 129, 0.6)",
    },
    text: {
      scale: 0.8,
      backgroundColor: "rgba(79, 172, 254, 0.1)",
      border: "2px solid rgba(79, 172, 254, 0.4)",
    },
    view: {
      scale: 2,
      backgroundColor: "rgba(239, 68, 68, 0.1)",
      border: "2px solid rgba(239, 68, 68, 0.4)",
    },
  };

  // Hide on mobile devices
  if (typeof window !== "undefined" && window.innerWidth < 768) {
    return null;
  }

  return (
    <motion.div
      className="fixed top-0 left-0 w-8 h-8 rounded-full pointer-events-none z-50 mix-blend-difference"
      style={{
        left: x - 16,
        top: y - 16,
      }}
      animate={cursorVariant}
      variants={variants}
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 28,
      }}
    >
      <AnimatePresence>
        {cursorText && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="absolute top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
          >
            {cursorText}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trailing dots */}
      <div className="absolute inset-0">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-primary-400 rounded-full"
            style={{
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            animate={{
              scale: [0, 1, 0],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default CustomCursor;
